import { useMemo, useState, useRef } from "react";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { generateTimeOptions, formatDuration } from "./TimePickerField.utils";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);

const useHyroTimePickerField = ({
  value,
  onChange,
  fromTime = null,
  minutesStep = 15,
  timeConstraints,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const selectedRef = useRef(null);
  const shouldScrollToSelectedOption = useRef(true);

  const baseTimeOptions = useMemo(() => generateTimeOptions({ minutesStep }), [minutesStep]);

  const timeOptions = useMemo(() => {
    let filteredOptions = baseTimeOptions;

    // Apply time constraints if provided
    if (timeConstraints?.min || timeConstraints?.max) {
      const minTime = timeConstraints.min ? dayjs(timeConstraints.min, "HH:mm") : null;
      const maxTime = timeConstraints.max ? dayjs(timeConstraints.max, "HH:mm") : null;

      filteredOptions = baseTimeOptions.filter(option => {
        const optionTime = option.value;

        // Check min constraint
        if (minTime && optionTime.isBefore(minTime, "minute")) {
          return false;
        }

        // Check max constraint
        if (maxTime && optionTime.isAfter(maxTime, "minute")) {
          return false;
        }

        return true;
      });
    }

    // Apply fromTime filtering if provided
    if (!fromTime) return filteredOptions;

    const fromNorm = dayjs(fromTime, "HH:mm");
    return filteredOptions
      .map(option => {
        const optNorm = option.value;
        const diff = optNorm.diff(fromNorm, "minute");
        if (diff <= 0) return null;
        return {
          ...option,
          label: `${option.label} (${formatDuration(diff)})`,
          duration: diff,
          formattedDuration: formatDuration(diff),
        };
      })
      .filter(Boolean);
  }, [baseTimeOptions, fromTime, timeConstraints]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    shouldScrollToSelectedOption.current = true;
    if (isEditMode) {
      // If in edit mode, close edit mode and don't open dropdown
      setIsEditMode(false);
      setInputValue("");
    } else {
      // If not in edit mode, enter edit mode
      setIsEditMode(true);
      setInputValue(value ? value.format("hh:mm a") : "");
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setIsEditMode(false);
  };

  const handleTimeSelect = (option) => {
    const newValue = value
      ? value.hour(option.value.hour()).minute(option.value.minute())
      : option.value;
    onChange(newValue.format("HH:mm"));
    handleClose();
  };

  const parseTimeInput = (input) => {
    // Remove all non-alphanumeric characters except : and .
    const cleaned = input.replace(/[^a-zA-Z0-9:.]/g, "").toLowerCase();

    // Handle various time formats
    if (/^\d{1,2}:\d{2}$/.test(cleaned)) {
      // HH:MM format
      const [hours, minutes] = cleaned.split(":").map(Number);
      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        return dayjs().hour(hours).minute(minutes);
      }
    } else if (/^\d{1,2}:\d{2}[ap]m?$/.test(cleaned)) {
      // HH:MM AM/PM format
      const timeStr = cleaned.replace(/(\d{1,2}):(\d{2})([ap]m?)/, "$1:$2 $3");
      const parsed = dayjs(timeStr, "h:mm a");
      if (parsed.isValid()) {
        return parsed;
      }
    } else if (/^\d{1,2}[ap]m?$/.test(cleaned)) {
      // HH AM/PM format
      const timeStr = cleaned.replace(/(\d{1,2})([ap]m?)/, "$1:00 $2");
      const parsed = dayjs(timeStr, "h:mm a");
      if (parsed.isValid()) {
        return parsed;
      }
    } else if (/^\d{1,2}$/.test(cleaned)) {
      // HH format (assume AM)
      const hours = parseInt(cleaned);
      if (hours >= 0 && hours <= 23) {
        return dayjs().hour(hours).minute(0);
      }
    } else if (/^\d{1,2}\.\d{1,2}$/.test(cleaned)) {
      // HH.MM format
      const [hours, minutes] = cleaned.split(".").map(Number);
      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        return dayjs().hour(hours).minute(minutes);
      }
    }

    return null;
  };

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const handleInputBlur = () => {
    const parsedTime = parseTimeInput(inputValue);
    if (parsedTime && parsedTime.isValid() && !parsedTime.isSame(value, "minute")) {
      onChange(parsedTime.format("HH:mm"));
    }
    setIsEditMode(false);
    setInputValue("");
  };

  const handleInputKeyDown = (event) => {
    if (event.key === "Enter") {
      const parsedTime = parseTimeInput(inputValue);
      if (parsedTime && parsedTime.isValid()) {
        onChange(parsedTime.format("HH:mm"));
      }
      setIsEditMode(false);
      setInputValue("");
    } else if (event.key === "Escape") {
      setIsEditMode(false);
      setInputValue("");
    }
  };

  const duration = useMemo(() => {
    if (!fromTime || !value) return null;
    const diff = value.diff(fromTime, "minute");
    return diff > 0 ? formatDuration(diff) : null;
  }, [fromTime, value]);

  const displayValue = isEditMode ? inputValue : (value ? value.format("hh:mm a") : "");

  const getIsSelected = (option) =>
    value?.hour() === option.value.hour() && value?.minute() === option.value.minute();

  const handleSelectedRef = node => {
    if (node && shouldScrollToSelectedOption.current) {
      selectedRef.current = node;
      node.scrollIntoView({ block: "center" });

      // This flag prevents scrolling to selected option on every click
      shouldScrollToSelectedOption.current = false;
    }
  };

  return {
    anchorEl,
    isOpen: Boolean(anchorEl),
    isEditMode,
    duration,
    displayValue,
    timeOptions,
    handleClick,
    handleClose,
    handleTimeSelect,
    handleInputChange,
    handleInputBlur,
    handleInputKeyDown,
    getIsSelected,
    handleSelectedRef
  };
};

export default useHyroTimePickerField;
